import { Transition } from '@mantine/core';
import { useEffect, useState } from 'react';

interface AnimatedItemProps {
  /** Render prop (not plain children) so the caller controls the actual markup — Transition only
   * hands back styles to apply, it can't wrap the caller's element (e.g. a `<Table.Tr>`) in an
   * extra DOM node without breaking that element's structure. */
  children: (styles: React.CSSProperties) => React.ReactElement;
  /** Fires once the exit (fade-out) animation finishes — the caller's actual removal from its own
   * data/state should happen here, not before, so the element stays visible while it fades. */
  onExited?: () => void;
  /** Flip to `false` to play the exit animation before the element is actually removed from data. */
  visible: boolean;
}

/**
 * Fades a single element in on mount and out before removal — used by `PriceComponentsTable` and
 * `FormulaRowFields` so adding/removing a table row animates instead of popping in/out instantly,
 * and by `FormulaTestingPanel` for the same fade on its (non-table) per-spec input blocks. Nothing
 * here is table-specific — it only hands back styles for the caller's own markup.
 */
export const AnimatedItem: React.FC<AnimatedItemProps> = ({ children, onExited, visible }) => {
  const [mounted, setMounted] = useState(false);

  // Starts unmounted so Transition has a prior render to animate from — flipping this true one
  // frame after mount plays the enter (fade-in) transition instead of rendering already visible.
  // Deferred via rAF (rather than set directly in the effect body) so React doesn't treat this as
  // a synchronous re-render loop.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setMounted(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <Transition duration={180} mounted={mounted && visible} onExited={onExited} transition="fade">
      {children}
    </Transition>
  );
};
